/**
 * "Is this the same person?" — the one component both identity-review surfaces
 * use (cca-yb68, ADR-064): the registrar dashboard's queue card and the paper
 * route's inline 409. The server hands both the SAME candidates payload, so
 * one component serves both and the registrar sees one screen, not two.
 *
 * The interaction law (Miranda §2), which is the defect this stream fixes:
 *  - the evidence decides and the interface never recommends: two EQUAL
 *    `.btn-ghost` answers, in a fixed order, neither of them primary and
 *    nothing preselected;
 *  - friction goes where a third party can be harmed (SET-4) — Link files
 *    Police results on someone else's record, so it takes an inline
 *    confirmation; Create touches nobody else and acts at once;
 *  - no state is carried by colour alone: every facet has a glyph AND a word.
 *
 * Focus (Miranda §3b, §3g): confirming moves focus to the prompt; Back or
 * Escape returns it to the Yes button; an error keeps focus where it was and
 * speaks through `role="alert"` inside the card.
 */

import { useCallback, useEffect, useId, useRef, useState, type JSX, type ReactNode } from 'react';
import {
  BUSY_LABELS,
  CHOOSE_FIRST,
  COPY,
  FACET_WORDS,
  LEAVE_AS_IS,
  LINKED_NOW,
  MORE_PEOPLE_NOTE,
  PLACEHOLDER_COLUMN,
  QUESTION,
  SEVERAL_PEOPLE_LEGEND,
  agreementStrip,
  alreadyLinkedNote,
  answerLabels,
  comparisonRows,
  confirmCloseFocusTarget,
  facetPillClass,
  hintSentences,
  linkConfirmText,
  namePills,
  orderCandidatesForDisplay,
  otherApplications,
  type CandidatesPayload,
  type ConfirmExitReason,
  type DobFacet,
  type LinkConfirmCopy,
  type ReviewCandidate,
  type TypedApplication,
} from './identity-review';

/** Deep link to a Contact in the CCAANZ Zoho org — the same org the worker's
 *  result-ambiguity alert links (vetting-crons.ts:1079). An org id is config,
 *  not a secret. */
const CRM_CONTACT_URL = (contactId: string) =>
  `https://crm.zoho.com.au/crm/org7006827524/tab/Contacts/${contactId}`;

export interface IdentityChoice {
  action: 'link' | 'create';
  /** Null for create; a Zoho Contact id for link. */
  contactId: string | null;
  /** Quoted back so the server can refuse a stale set (409 record_changed). */
  version: string;
}

export interface IdentityChooserProps {
  /** What was typed — always the left column, never rewritten from CRM. */
  typed: TypedApplication;
  parishName: string;
  payload: CandidatesPayload | null;
  loading?: boolean;
  loadError?: string | null;
  onRetryLoad?: () => void;
  busy: 'link' | 'create' | null;
  /** An inline, announced failure. Focus stays on the button that was pressed. */
  error?: string | null;
  onChoose: (choice: IdentityChoice) => void;
  /** The discrete footer (email the applicant, discard, received date). */
  footer?: ReactNode;
  /**
   * The words of the inline Link confirmation. Defaults to `linkConfirmText`
   * (a FIRST link, from the review queue); the R1 move passes
   * `relinkConfirmText`, because a move emails nobody and does not send the
   * application to the parish. The interaction is identical either way, which
   * is the point of one component (Miranda: one chooser).
   */
  confirmCopy?: LinkConfirmCopy;
  /** Verbs for the two busy states — "Linking…" here, "Moving…" on a move. */
  busyLabels?: { link: string; create: string };
  /**
   * The Contact this application is linked to NOW, when it already has one.
   * Null on a first link (a held item has no Contact by design, ADR-064 §2).
   * The chooser marks that person in the list, and refuses to dress a no-op up
   * as a decision when they are the only candidate.
   */
  currentContactId?: string | null;
  /**
   * "Leave it as it is" — a way OUT of the chooser that decides nothing
   * (Miranda S8). Only the surfaces that opened the chooser on purpose pass it:
   * the R1 move, where the panel is a disclosure the registrar can have opened
   * by mistake. The held-item queue and the paper route do NOT, because there
   * the chooser is the work itself and leaving it is not an option.
   *
   * Two routes to it, and they are the same route: a ghost button beside the
   * Create answer in the single-candidate dead end (where Create would mint a
   * duplicate CRM person and was the only control in reach), and Escape when no
   * confirmation is open. The caller decides WHEN Escape counts — see
   * RelinkControl, which only honours it while focus is in its own panel.
   */
  onDismiss?: () => void;
}

function FacetPill({ label, facet }: { label: string; facet: DobFacet }): JSX.Element {
  const { glyph, word } = FACET_WORDS[facet];
  return (
    <span className={`pill ${facetPillClass(facet)}`}>
      <span aria-hidden="true">{glyph}</span>
      {label ? `${label}: ${word}` : word}
    </span>
  );
}

function CandidateCards({
  candidates,
  chosenId,
  onChoose,
  groupName,
  moreCandidates,
  currentContactId,
}: {
  candidates: ReviewCandidate[];
  chosenId: string | null;
  onChoose: (id: string) => void;
  groupName: string;
  moreCandidates: boolean;
  currentContactId: string | null;
}): JSX.Element {
  return (
    <fieldset className="idr-cands">
      <legend className="idr-legend">{SEVERAL_PEOPLE_LEGEND(candidates.length)}</legend>
      {candidates.map((c, i) => (
        <div className="idr-cand" key={c.contactId}>
          <label className="idr-cand-label">
            <input
              type="radio"
              name={groupName}
              value={c.contactId}
              checked={chosenId === c.contactId}
              onChange={() => onChoose(c.contactId)}
              data-idr-radio={i === 0 ? 'first' : undefined}
            />
            <span className="idr-cand-body">
              <span className="idr-cand-name">
                {c.name || 'No name on record'}
                {/* Which one is he choosing AWAY from? A word, not a colour or
                    a position — the list is re-ordered by agreement. */}
                {c.contactId === currentContactId && (
                  <span className="pill pill-neutral idr-cand-now">{LINKED_NOW}</span>
                )}
              </span>
              <span className="idr-cand-facets">
                <FacetPill label="Date of birth" facet={c.facets.dob} />
                {namePills(c.facets).map((p) => (
                  <FacetPill key={p.label} label={p.label} facet={p.facet} />
                ))}
              </span>
              {c.applications && (
                <span className="idr-cand-apps">
                  {c.applications.count}{' '}
                  {c.applications.count === 1 ? 'application' : 'applications'}
                </span>
              )}
            </span>
          </label>
          <a
            className="idr-link"
            href={CRM_CONTACT_URL(c.contactId)}
            target="_blank"
            rel="noopener"
            aria-label={COPY.crmLink(c.name || 'this person')}
          >
            Open CRM record ↗
          </a>
        </div>
      ))}
      {moreCandidates && <p className="idr-note">{MORE_PEOPLE_NOTE}</p>}
    </fieldset>
  );
}

export function IdentityChooser({
  typed,
  parishName,
  payload,
  loading = false,
  loadError = null,
  onRetryLoad,
  busy,
  error = null,
  onChoose,
  footer,
  confirmCopy = linkConfirmText,
  busyLabels = BUSY_LABELS,
  currentContactId = null,
  onDismiss,
}: IdentityChooserProps): JSX.Element {
  const uid = useId();
  const [chosenId, setChosenId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [needChoice, setNeedChoice] = useState(false);
  const yesRef = useRef<HTMLButtonElement | null>(null);
  const promptRef = useRef<HTMLParagraphElement | null>(null);
  const questionRef = useRef<HTMLHeadingElement | null>(null);
  const alertRef = useRef<HTMLParagraphElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  /** Why the prompt is closing, set by whichever path closes it and consumed
   *  once by the focus effect. Null means it closed for neither reason (the
   *  first render), and nothing moves. */
  const exitReason = useRef<ConfirmExitReason | null>(null);
  /** The last COMMITTED value of `confirming`, so the payload effect below can
   *  tell "the server replaced the evidence under an open prompt" from "a
   *  payload arrived while no prompt was open". */
  const confirmingRef = useRef(false);

  const version = payload?.version ?? '';
  const ordered = payload ? orderCandidatesForDisplay(payload.candidates) : [];
  const several = ordered.length > 1;
  const only = !several ? ordered[0] : undefined;
  const subject = several
    ? (ordered.find((c) => c.contactId === chosenId) ?? null)
    : (only ?? null);

  // A fresh payload (a 409 record_changed reload) is a fresh decision: forget
  // the choice and close the confirmation rather than re-applying consent that
  // was given against a set that has since moved. Closing it this way is a
  // REFUSAL — the button the registrar pressed is destroyed with the prompt —
  // so focus has to be placed, not merely left alone.
  useEffect(() => {
    setChosenId(null);
    setNeedChoice(false);
    if (confirmingRef.current) exitReason.current = 'refused';
    setConfirming(false);
  }, [version]);

  /** Back and Escape are the same route out, and neither may focus anything
   *  itself: while the prompt is open the Yes button is UNMOUNTED, so React has
   *  already nulled its ref and a `.focus()` in the handler is a silent no-op
   *  that drops the keyboard to the top of the page. The effect below does it
   *  after the render that puts the button back. */
  const closeConfirm = useCallback(() => {
    exitReason.current = 'back';
    setConfirming(false);
  }, []);

  /**
   * ONE Escape handler with two branches, and they are mutually exclusive by
   * construction: while a confirmation is open Escape belongs to the
   * confirmation (it is the nearer thing, and it is the one holding a filled
   * button); with no confirmation open it belongs to the panel, if the caller
   * offers a way out at all. Two listeners would have raced to answer the same
   * keystroke and closed both.
   */
  useEffect(() => {
    if (!confirming && !onDismiss) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (confirming) {
        closeConfirm();
        return;
      }
      onDismiss?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [confirming, closeConfirm, onDismiss]);

  // Focus follows the confirmation in BOTH directions (Miranda §3b, §3g), and
  // this effect runs AFTER the render, so whichever element it aims at exists.
  useEffect(() => {
    confirmingRef.current = confirming;
    if (confirming) {
      // A close decided in THIS commit (the payload effect above runs first):
      // don't take focus into a prompt that is about to disappear.
      if (exitReason.current) return;
      promptRef.current?.focus();
      return;
    }
    const reason = exitReason.current;
    if (!reason) return;
    exitReason.current = null;
    const target = confirmCloseFocusTarget({
      reason,
      alertRendered: alertRef.current !== null,
      yesButtonRendered: yesRef.current !== null,
    });
    const el =
      target === 'error-alert'
        ? alertRef.current
        : target === 'yes-button'
          ? yesRef.current
          : questionRef.current;
    el?.focus();
  }, [confirming]);

  if (loading) return <p className="idr-note">{COPY.candidatesLoading}</p>;

  if (loadError) {
    return (
      <div>
        <p className="idr-error" role="alert">
          {loadError}
        </p>
        {onRetryLoad && (
          <button type="button" className="btn btn-ghost" onClick={onRetryLoad}>
            {COPY.tryAgain}
          </button>
        )}
      </div>
    );
  }

  if (!payload) return <p className="idr-note">{COPY.candidatesLoading}</p>;

  const labels = answerLabels(subject?.name ?? null);
  const rows = comparisonRows(typed, subject, parishName);
  const hints = hintSentences(subject ? subject.hints : payload.hints);
  const other = subject ? otherApplications(subject) : null;
  const nobodyOnRecord = ordered.length === 0;
  /** The one candidate IS the person it is already on: "Yes, move to …" would
   *  be a no-op behind a confirmation. Say so, and leave Create as the only
   *  answer that does anything. */
  const onlyPersonIsCurrent =
    !several &&
    only !== undefined &&
    currentContactId !== null &&
    only.contactId === currentContactId;

  function pressLink() {
    if (several && !chosenId) {
      setNeedChoice(true);
      rootRef.current?.querySelector<HTMLInputElement>('[data-idr-radio="first"]')?.focus();
      return;
    }
    setNeedChoice(false);
    setConfirming(true);
  }

  return (
    <div className="idr-chooser" aria-busy={busy !== null} ref={rootRef}>
      {subject && (
        <ul className="idr-strip">
          {agreementStrip(subject.facets).map((s) => (
            <li key={s.label}>
              <FacetPill label={s.label} facet={s.facet} />
            </li>
          ))}
        </ul>
      )}

      {several && (
        <CandidateCards
          candidates={ordered}
          chosenId={chosenId}
          groupName={`${uid}-candidate`}
          moreCandidates={payload.moreCandidates}
          currentContactId={currentContactId}
          onChoose={(id) => {
            setChosenId(id);
            setNeedChoice(false);
            setConfirming(false);
          }}
        />
      )}

      {nobodyOnRecord && <p className="idr-note">{COPY.noCandidates}</p>}

      <div className="idr-cols" aria-hidden="true">
        <span>{COPY.typedColumn}</span>
        <span>{several && !subject ? PLACEHOLDER_COLUMN : COPY.crmColumn}</span>
      </div>

      <div className="idr-fields">
        {rows.map((r) => (
          <div className="idr-field" key={r.label}>
            <span className="idr-label">{r.label}</span>
            {r.facet && <FacetPill label="" facet={r.facet} />}
            <span className="idr-side">
              <span className="idr-sidelabel">{r.shared ? 'Typed (on both)' : 'Typed'}</span>
              <span className="idr-value">{r.typed}</span>
            </span>
            {r.onRecord !== null && (
              <span className="idr-side">
                <span className="idr-sidelabel">On record</span>
                <span className="idr-value">{r.onRecord}</span>
              </span>
            )}
          </div>
        ))}
      </div>

      {subject && (
        <p className="idr-crmlink">
          <a
            className="idr-link"
            href={CRM_CONTACT_URL(subject.contactId)}
            target="_blank"
            rel="noopener"
            aria-label={COPY.crmLink(subject.name || 'this person')}
          >
            Open CRM record ↗
          </a>
        </p>
      )}

      {hints.length > 0 && (
        <ul className="idr-hints">
          {hints.map((h) => (
            <li key={h}>{h}</li>
          ))}
        </ul>
      )}

      {other && (
        <div className="idr-other">
          <p className="idr-label">{other.heading}</p>
          <ul>
            {other.lines.map((l) => (
              <li key={l}>{l}</li>
            ))}
            {other.more && <li>{other.more}</li>}
          </ul>
        </div>
      )}

      {/* tabIndex -1: not in the tab order, but focusable as the fallback when
          the confirmation closes on a card that has no Yes button. */}
      <h4 className="idr-question" tabIndex={-1} ref={questionRef}>
        {QUESTION}
      </h4>

      {confirming && subject ? (
        <ConfirmLink
          crmName={subject.name || 'this person'}
          typedName={typed.applicantName}
          parishName={parishName}
          copy={confirmCopy}
          busyLabel={busyLabels.link}
          busy={busy === 'link'}
          promptRef={promptRef}
          onBack={closeConfirm}
          onConfirm={() => onChoose({ action: 'link', contactId: subject.contactId, version })}
        />
      ) : (
        <div className="idr-actions">
          {/* Two EQUAL ghost buttons in a fixed order. `aria-disabled` rather
              than `disabled` while busy: focus is kept where the registrar put
              it, and a second press does nothing. */}
          {onlyPersonIsCurrent && (
            <p className="idr-note idr-already">{alreadyLinkedNote(only?.name || 'this person')}</p>
          )}
          {!nobodyOnRecord && !onlyPersonIsCurrent && (
            <button
              type="button"
              className="btn btn-ghost"
              ref={yesRef}
              aria-disabled={busy !== null}
              onClick={() => {
                if (busy === null) pressLink();
              }}
            >
              {busy === 'link' ? busyLabels.link : labels.link}
            </button>
          )}
          <button
            type="button"
            className="btn btn-ghost"
            aria-disabled={busy !== null}
            onClick={() => {
              // Create touches nobody else and is safe to retry (the server
              // reuses the Contact it made), so it acts at once.
              if (busy === null) onChoose({ action: 'create', contactId: null, version });
            }}
          >
            {busy === 'create' ? busyLabels.create : labels.create}
          </button>
          {/* The single-candidate dead end: the only person on the mailbox IS
              the person it is already on, so Create was the sole control in
              reach — at 390px, 1,300px below the toggle that opened the panel —
              and it mints a duplicate CRM person. A way out that decides
              nothing, placed AFTER the answer so the answer order is untouched
              and this does not read as a recommendation (Miranda S8). */}
          {onlyPersonIsCurrent && onDismiss && (
            <button
              type="button"
              className="btn btn-ghost"
              // `aria-disabled`, not `disabled`, and a guard in the handler —
              // the same rule as its siblings above: focus stays where the
              // registrar put it, and leaving mid-create is not offered.
              aria-disabled={busy !== null}
              onClick={() => {
                if (busy === null) onDismiss();
              }}
            >
              {LEAVE_AS_IS}
            </button>
          )}
        </div>
      )}

      {needChoice && (
        <p className="idr-error" role="alert">
          {CHOOSE_FIRST}
        </p>
      )}
      {/* The server's refusal: announced by role="alert", and focusable
          (tabIndex -1) because a 409 record_changed destroys the button that
          was pressed — this is where the registrar is put instead. */}
      {error && (
        <p className="idr-error" role="alert" tabIndex={-1} ref={alertRef}>
          {error}
        </p>
      )}
      {footer && <div className="idr-footer">{footer}</div>}
    </div>
  );
}

/** The inline Link confirmation: a plain `.btn` pair in the card, not a modal
 *  and not the Settings drawer's confirm primitive — SET-3 keeps those on
 *  settings surfaces, and that one also drops focus when it swaps modes. */
function ConfirmLink({
  crmName,
  typedName,
  parishName,
  copy,
  busyLabel,
  busy,
  promptRef,
  onBack,
  onConfirm,
}: {
  crmName: string;
  typedName: string;
  parishName: string;
  copy: LinkConfirmCopy;
  busyLabel: string;
  busy: boolean;
  promptRef: React.RefObject<HTMLParagraphElement | null>;
  onBack: () => void;
  onConfirm: () => void;
}): JSX.Element {
  const t = copy({ crmName, typedName, parishName });
  return (
    <div className="idr-confirm">
      {/* tabIndex -1: focus lands here when the confirmation opens, so the
          consequences are read before the filled button is reachable. */}
      <p className="idr-confirm-head" tabIndex={-1} ref={promptRef}>
        {t.heading}
      </p>
      {/* Above the body, not inside it, and not last: the registrar's own
          ruling of 2026-09-18 is that this sentence must be unmissable, because
          the status the lock reads only flips when he presses "Mark lodged" —
          hours after he keyed the application into the NZPV portal. It is not
          carried by colour: it is the first thing after the question, in its
          own block, in bold. */}
      {t.warn && <p className="idr-confirm-warn">{t.warn}</p>}
      {t.body.map((line) => (
        <p className="idr-confirm-body" key={line}>
          {line}
        </p>
      ))}
      <div className="idr-actions">
        {/* Once the decision is made, a filled button is right. */}
        <button
          type="button"
          className="btn btn-primary"
          aria-disabled={busy}
          onClick={() => {
            if (!busy) onConfirm();
          }}
        >
          {busy ? busyLabel : t.confirm}
        </button>
        <button type="button" className="btn btn-ghost" aria-disabled={busy} onClick={onBack}>
          {t.back}
        </button>
      </div>
    </div>
  );
}
